import { gsap } from 'gsap'
import { ResizeCallback } from '../utils/resize-callback.js'
/**
 * Marquee Component
 *
 * Expected CSS structure (based on actual HTML):
 *
 * .component--logos {
 *   overflow: clip;
 * }
 *
 * .marquee {
 *   // marquee container
 * }
 *
 * .marquee__track {
 *   --gap: 50px;
 *   display: flex;
 *   white-space: nowrap;
 *   flex-direction: row;
 * }
 *
 * .marquee__row {
 *   display: flex;
 *   white-space: nowrap;
 *   gap: var(--gap);
 *   flex: 0 0 auto;
 *   width: auto;
 *   padding-right: var(--gap);
 * }
 *
 * .logos__item {
 *   position: relative;
 *   flex: 0 0 auto;
 *   width: auto;
 *
 *   img {
 *     display: block;
 *     height: 13vw;
 *   }
 *
 *   a {
 *     position: absolute;
 *     inset: 0;
 *   }
 *
 *   &:has(a):hover {
 *     opacity: 0.7;
 *   }
 * }
 *
 * Required HTML structure:
 * <div class="component--logos js-logos">
 *   <div class="logos marquee js-marquee">
 *     <div class="logos__wrapper marquee__track js-marquee-track">
 *       <div class="logos__inner marquee__row js-marquee-row">
 *         <div class="logos__item">
 *           <img src="..." />
 *           <a href="..." class="logos__item__link"></a>
 *         </div>
 *       </div>
 *     </div>
 *   </div>
 * </div>
 */
export class Marquee {
	constructor(el, options = {}) {
		this.el = el

		this.options = {
			speed: 60, // px/sec
			pauseOnHover: true, // enable hover pause
			...options,
		}

		this.track = this.el.querySelector('.js-marquee-track')
		this.row = this.el.querySelector('.js-marquee-row')

		this.clone = null
		this.tween = null

		this.setup()
		this.createTimeline()
		this.bindEvents()
	}

	// ------------------------------------------------------------ //
	// SETUP
	// ------------------------------------------------------------ //
	setup() {
		// clone row for seamless looping
		this.clone = this.row.cloneNode(true)
		this.track.appendChild(this.clone)

		// make logos clickable
		this.track.style.pointerEvents = 'auto'

		// scroll container
		this.track.style.display = 'flex'
		this.track.style.whiteSpace = 'nowrap'

		this.buildClones()
		this.recalculate()
	}

	buildClones() {
		// remove any previous clones
		Array.from(this.track.children).forEach((child) => {
			if (child !== this.row) child.remove()
		})

		// ensure layout is measured after cleanup
		const containerWidth = this.el.offsetWidth
		let totalWidth = this.row.offsetWidth

		// duplicate full row until wider than container
		while (totalWidth < containerWidth) {
			const clone = this.row.cloneNode(true)
			this.track.appendChild(clone)
			totalWidth += clone.offsetWidth
		}

		// add one extra full row for seamless looping
		const extra = this.row.cloneNode(true)
		this.track.appendChild(extra)
	}

	recalculate() {
		this.rowWidth = this.row.offsetWidth
		this.duration = this.rowWidth / this.options.speed // seconds
	}

	// ------------------------------------------------------------ //
	// GSAP
	// ------------------------------------------------------------ //
	createTimeline() {
		if (this.tween) this.tween.kill()

		this.tween = gsap.to(this.track, {
			x: -this.rowWidth,
			duration: this.duration,
			ease: 'none',
			repeat: -1,
			modifiers: {
				x: gsap.utils.unitize((v) => parseFloat(v) % -this.rowWidth),
			},
		})
	}

	// ------------------------------------------------------------ //
	// EVENTS
	// ------------------------------------------------------------ //
	bindEvents() {
		// Use ResizeCallback instead of direct resize listener
		this.resizeCallback = new ResizeCallback(
			(data) => {
				// Only recalculate if width changed (x-axis)
				this.buildClones()

				this.recalculate()
				this.createTimeline()
			},
			{
				delay: 150, // slightly longer delay for performance
				resizeThreshold: 10, // only trigger if width changes by at least 10px
				detectAxis: ['x'], // only detect horizontal changes
			},
		)

		if (this.options.pauseOnHover) {
			this.el.addEventListener('mouseenter', this.pause)
			this.el.addEventListener('mouseleave', this.resume)
		}
	}

	pause = () => {
		if (!this.tween) return

		gsap.to(this.tween, {
			timeScale: 0,
			duration: 1, // how long the slowdown takes
			ease: 'power2.out',
		})
	}

	resume = () => {
		if (!this.tween) return

		gsap.to(this.tween, {
			timeScale: 1,
			duration: 1, // speed-up duration
			ease: 'power2.out',
		})
	}

	// ------------------------------------------------------------ //
	// CLEANUP
	// ------------------------------------------------------------ //
	destroy() {
		// Clean up ResizeCallback instead of direct event listener
		if (this.resizeCallback) {
			this.resizeCallback.destroy()
			this.resizeCallback = null
		}

		if (this.options.pauseOnHover) {
			this.el.removeEventListener('mouseenter', this.pause)
			this.el.removeEventListener('mouseleave', this.resume)
		}

		if (this.tween) this.tween.kill()
	}
}
